import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const TAKING_OVER = [
  {
    q: "What actually happens when I take over someone's shop lease?",
    a: "The lease is assigned to you. You step into the outgoing tenant's shoes for the rest of the term, on the same rent, clauses and options. The landlord has to consent, and in Queensland that consent process under the Retail Shop Leases Act 1994 involves disclosure statements, a financial advice report from your accountant and a Form 12 legal advice report signed by a lawyer.",
  },
  {
    q: "Do I really need a Form 12?",
    a: "If the premises are a retail shop under the Act, the landlord will usually want the Form 12 before it consents. More importantly, it's the one point in the deal where someone independent goes through the lease with you. We prepare the Form 12 together with a full lease review for a fixed $880 including GST, usually within 1 to 2 business days.",
  },
  {
    q: "What is a redecoration or refurbishment clause, and why does it matter?",
    a: "This is the one that catches people most. Many leases require the tenant to refit or redecorate the shop at set times, or when the landlord asks. If you buy a business two years into a lease that requires a refit at year three, that cost is yours. It can run to tens of thousands of dollars. We check whether the clause exists, when it bites, whether it was properly disclosed, and factor it into what you should pay for the business.",
  },
  {
    q: "What is a demolition or relocation clause?",
    a: "It lets the landlord end the lease, or move you, if the centre or building is redeveloped. The Act puts limits on how these clauses work and when compensation may be payable, but a business with five years left on paper may have much less in practice. We tell you whether the clause is there and what you would realistically be entitled to.",
  },
  {
    q: "Is the outgoing tenant's option to renew still available to me?",
    a: "Usually yes, the option comes with the lease. But an option only counts if it is exercised properly: in writing, within the window the lease sets, and often only if you haven't been in breach. Miss the window by a day and it can be lost. We diary the dates for you and explain exactly how to exercise it.",
  },
  {
    q: "Am I responsible for anything the old tenant did wrong?",
    a: "Possibly. Unpaid rent, outstanding outgoings or unauthorised fit-out can become a dispute between you and the landlord. We ask for the arrears position and the landlord's consent documents to confirm there is nothing outstanding before you settle.",
  },
  {
    q: "Do I have to give a personal guarantee?",
    a: "Most landlords ask for one, plus a bank guarantee or bond. The amount and scope can often be negotiated before consent is given. It is worth knowing exactly what you are personally signing up for before you commit.",
  },
];

const NEW_LEASE = [
  {
    q: "What should I receive before signing a new retail shop lease?",
    a: "The landlord must give you a disclosure statement (Form 7) and a copy of the proposed lease before you sign, generally at least 7 days beforehand. It sets out rent, outgoings, fit-out obligations, redevelopment plans and more. If it is late, wrong or missing, you may have rights later, so keep it.",
  },
  {
    q: "What does percentage or turnover rent mean?",
    a: "Some leases, particularly in shopping centres, charge base rent plus a percentage of your sales above a threshold. It means you report turnover to the landlord and pay more in good years. We explain how the threshold is set, what counts as turnover (online sales, gift cards, GST) and what reporting you are committing to.",
  },
  {
    q: "What outgoings will I pay, and what's included?",
    a: "Outgoings can include council rates, water, insurance, cleaning, air-conditioning, security, management fees and marketing levies. The disclosure statement should estimate them. We check what is actually recoverable, how it is apportioned between tenants and what rights you have to see the landlord's accounts.",
  },
  {
    q: "How do rent reviews work?",
    a: "Common mechanisms are CPI, a fixed percentage each year, or a market review. Some leases stack them. We model what the rent could look like over the full term, including option periods, so you can decide whether the business still works.",
  },
  {
    q: "What happens at the end of the lease?",
    a: "Make-good clauses can require you to strip out your fit-out and return the premises to a bare shell. Combined with a redecoration clause, this is often the largest hidden cost in a lease. We tell you what you are signing up for now, not when you leave.",
  },
];

const ALL_FAQS = [...TAKING_OVER, ...NEW_LEASE];

export const Route = createFileRoute("/retail-shop-lease-faqs")({
  head: () => ({
    meta: [
      { title: "Taking Over a Shop Lease QLD: FAQs | Coastside Law" },
      {
        name: "description",
        content:
          "Taking over a QLD shop lease? Redecoration, demolition, turnover rent, options and outgoings explained. Lease review + Form 12: $880 incl GST.",
      },
      { property: "og:title", content: "Taking over or signing a shop lease in Queensland: FAQs" },
      {
        property: "og:description",
        content:
          "The lease clauses that catch Queensland tenants out, explained plainly. Fixed-fee lease review and Form 12, 1 to 2 day turnaround.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/retail-shop-lease-faqs" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/retail-shop-lease-faqs" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(ALL_FAQS)) }],
  }),
  component: RetailLeaseFaqsPage,
});

function RetailLeaseFaqsPage() {
  return (
    <>
      <PageHero
        eyebrow="Retail shop leases"
        title="Taking over or signing a shop lease in Queensland"
        lead="The lease is often the biggest commitment in a small business, and the clauses that hurt are rarely the rent. Here are the questions we're asked most, and the traps we see. Lease review and Form 12 legal advice report: $880 incl GST, usually 1 to 2 business days."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <h2 className="font-serif text-xl text-foreground">The clauses that catch people out</h2>
          <ul className="mt-4 space-y-2.5">
            {[
              "Redecoration and refurbishment: a required refit you didn't budget for",
              "Demolition and relocation: a five-year lease that may not last five years",
              "Percentage turnover rent: paying more as sales grow",
              "Options: lost if not exercised correctly and on time",
              "Outgoings: what's included, and how it is shared",
              "Make-good: stripping the shop back at the end",
            ].map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Taking over an existing lease</h2>
        <div className="mt-8">
          <FaqList items={TAKING_OVER} />
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Signing a new retail lease</h2>
        <div className="mt-8">
          <FaqList items={NEW_LEASE} />
        </div>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">$880 incl GST, 1 to 2 days</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Send us the lease, the disclosure statement and any deed of assignment. We review the
            lease with you, flag the clauses above, and sign the Form 12 legal advice report for
            the landlord. Fixed fee, usually turned around within 1 to 2 business days.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/retail-shop-leases" className="text-primary underline">
              Form 12 legal advice reports
            </Link>
            <Link to="/business-sales" className="text-primary underline">
              Buying or selling a business
            </Link>
          </div>
        </div>

        <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
          General information only, not legal advice. Whether the Retail Shop Leases Act applies
          depends on the premises and the tenant.
        </p>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who does the work" />

        <div className="mt-16">
          <EnquiryForm heading="Send us your lease for review" />
        </div>
      </section>

      <CtaBand
        title="Lease in front of you?"
        body={`Call ${FIRM.phone} and we'll tell you what's needed and how quickly we can turn it around.`}
      />
      <div className="h-20" />
    </>
  );
}
